import { atom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";

/**
 * How the credential field puts its Save button in the artifact footer.
 *
 * The footer is a sibling of `<ArtifactView>`, not a descendant, so it cannot
 * read `ArtifactViewCtx` — and the block cannot render into it directly. What
 * crosses is a callback, never the credential: the typed value stays in the
 * block's own state and goes only to its own endpoint, which is the whole point
 * of the card. The footer learns a label, two booleans and a function to call.
 *
 * A single slot rather than a map. The footer exists on the lightbox, the canvas
 * card and the standalone page, and only one of those is in front of the user at
 * a time; the artifact id is carried so a footer showing a different card
 * ignores a stale registration rather than offering a Save that belongs to
 * something else.
 */
export type SecretSubmitRegistration = {
  artifactId: string;
  label: string;
  pendingLabel: string;
  canSubmit: boolean;
  isPending: boolean;
  submit: () => void;
};

const secretSubmitAtom = atom<SecretSubmitRegistration | null>(null);

/** Read the Save registered for this artifact, if the visible card is it. */
export const useSecretSubmit = (artifactId: string): SecretSubmitRegistration | null => {
  const registration = useAtomValue(secretSubmitAtom);
  return registration?.artifactId === artifactId ? registration : null;
};

/** Offer this card's Save to whichever footer is showing it. */
export const useRegisterSecretSubmit = (registration: SecretSubmitRegistration | null): void => {
  const setRegistration = useSetAtom(secretSubmitAtom);

  useEffect(() => {
    if (!registration) return;
    setRegistration(registration);

    return () => {
      // Only clear our own. Two cards can be mounted at once — the inline thread
      // copy under an open lightbox — and the one unmounting is not necessarily
      // the one the footer is currently reading.
      setRegistration((current) =>
        current?.artifactId === registration.artifactId ? null : current,
      );
    };
  }, [setRegistration, registration]);
};
